import { useState, useCallback } from 'react';
import { TabGroup, TabList, Tab, TabPanels, TabPanel } from '@headlessui/react';
import { SparklesIcon, ArrowPathIcon, ExclamationTriangleIcon, ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useAiSummary, useGenerateAiSummary } from '../hooks/useAiReleaseNotes';
import type { AiChangeItem, AiProductSection, AiCategoryDetail } from '../types/aiReleaseNotes';

interface AiReleaseNotesSummarySectionProps {
  releaseId: string;
}

function CategorySummaryBox({ detail, items, copyGetText }: { detail: AiCategoryDetail; items: AiChangeItem[]; copyGetText: () => string }) {
  const hasRisks = detail.risks.length > 0;
  const hasBreaking = detail.breakingChanges.length > 0;
  const hasItems = items.length > 0;

  return (
    <div className="mb-4 rounded-lg border border-indigo-200 bg-indigo-50/50 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold text-indigo-800 uppercase tracking-wide">Summary</h4>
        <CopyButton getText={copyGetText} />
      </div>
      {detail.summary && (
        <p className="text-sm text-gray-700 leading-relaxed">{detail.summary}</p>
      )}

      {hasItems && (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
          <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1">Changes</h4>
          <ul className="list-disc list-inside space-y-0.5">
            {items.map((item, i) => (
              <li key={i} className="text-sm text-slate-600">
                {item.product ? `[${item.product}] ` : ''}{item.title}
              </li>
            ))}
          </ul>
        </div>
      )}

      {hasBreaking && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3">
          <h4 className="text-xs font-semibold text-red-800 uppercase tracking-wide mb-1">Breaking Changes</h4>
          <ul className="list-disc list-inside space-y-0.5">
            {detail.breakingChanges.map((bc, i) => (
              <li key={i} className="text-sm text-red-700">{bc}</li>
            ))}
          </ul>
        </div>
      )}

      {hasRisks && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
          <h4 className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-1">Risks &amp; Concerns</h4>
          <ul className="list-disc list-inside space-y-0.5">
            {detail.risks.map((risk, i) => (
              <li key={i} className="text-sm text-amber-700">{risk}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function CopyButton({ getText }: { getText: () => string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(getText());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [getText]);

  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors"
    >
      {copied ? (
        <>
          <CheckIcon className="h-3.5 w-3.5 text-green-600" />
          Copied!
        </>
      ) : (
        <>
          <ClipboardDocumentIcon className="h-3.5 w-3.5" />
          Copy to clipboard
        </>
      )}
    </button>
  );
}

function formatCategoryText(label: string, detail: AiCategoryDetail | null | undefined, items: AiChangeItem[]): string {
  const lines: string[] = [`== ${label} ==`, ''];

  if (detail?.summary) {
    lines.push(detail.summary, '');
  }

  if (items.length > 0) {
    lines.push('CHANGES:');
    items.forEach(item => {
      const prefix = item.product ? `[${item.product}] ` : '';
      lines.push(`  - ${prefix}${item.title}`);
    });
    lines.push('');
  }

  if (detail?.breakingChanges && detail.breakingChanges.length > 0) {
    lines.push('BREAKING CHANGES:');
    detail.breakingChanges.forEach(bc => lines.push(`  - ${bc}`));
    lines.push('');
  }

  if (detail?.risks && detail.risks.length > 0) {
    lines.push('RISKS & CONCERNS:');
    detail.risks.forEach(r => lines.push(`  - ${r}`));
    lines.push('');
  }

  if (items.length > 0) {
    lines.push('DETAILS:');
    items.forEach(item => {
      const id = item.workItemId ? ` (#${item.workItemId})` : '';
      const prefix = item.product ? `[${item.product}] ` : '';
      lines.push(`  - ${prefix}${item.title}${id}: ${item.summary}`);
    });
  }

  return lines.join('\n');
}

function formatProductText(detail: AiCategoryDetail | null | undefined, sections: AiProductSection[]): string {
  const lines: string[] = ['== Product Changes ==', ''];

  if (detail?.summary) {
    lines.push(detail.summary, '');
  }

  const allItems = sections.flatMap(s => s.changes);
  if (allItems.length > 0) {
    lines.push('CHANGES:');
    allItems.forEach(item => {
      const prefix = item.product ? `[${item.product}] ` : '';
      lines.push(`  - ${prefix}${item.title}`);
    });
    lines.push('');
  }

  if (detail?.breakingChanges && detail.breakingChanges.length > 0) {
    lines.push('BREAKING CHANGES:');
    detail.breakingChanges.forEach(bc => lines.push(`  - ${bc}`));
    lines.push('');
  }

  if (detail?.risks && detail.risks.length > 0) {
    lines.push('RISKS & CONCERNS:');
    detail.risks.forEach(r => lines.push(`  - ${r}`));
    lines.push('');
  }

  sections.forEach(section => {
    lines.push(`[${section.productName}]`);
    section.changes.forEach(item => {
      const id = item.workItemId ? ` (#${item.workItemId})` : '';
      const prefix = item.product ? `[${item.product}] ` : '';
      lines.push(`  - ${prefix}${item.title}${id}: ${item.summary}`);
    });
    lines.push('');
  });

  return lines.join('\n');
}

function ChangeItemCard({ item }: { item: AiChangeItem }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-start justify-between gap-2">
        <h4 className="text-sm font-medium text-gray-900">
          {item.product && (
            <span className="inline-flex items-center mr-1.5 px-1.5 py-0.5 rounded text-xs font-semibold bg-indigo-100 text-indigo-700">
              {item.product}
            </span>
          )}
          {item.title}
        </h4>
        <div className="flex items-center gap-2 shrink-0">
          {item.workItemType && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
              {item.workItemType}
            </span>
          )}
          {item.workItemId && (
            <span className="text-xs text-gray-500">#{item.workItemId}</span>
          )}
        </div>
      </div>
      <p className="mt-1 text-sm text-gray-600">{item.summary}</p>
    </div>
  );
}

function ProductSection({ section }: { section: AiProductSection }) {
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-gray-700 border-b border-gray-200 pb-1">
        {section.productName}
      </h3>
      <div className="space-y-2">
        {section.changes.map((item, idx) => (
          <ChangeItemCard key={item.workItemId ?? idx} item={item} />
        ))}
      </div>
    </div>
  );
}

function EmptyTabContent({ message }: { message: string }) {
  return (
    <div className="text-center py-8 text-sm text-gray-500">
      {message}
    </div>
  );
}

export default function AiReleaseNotesSummarySection({ releaseId }: AiReleaseNotesSummarySectionProps) {
  const { data: summary, isLoading, error } = useAiSummary(releaseId);
  const generateMutation = useGenerateAiSummary();
  const [selectedTab, setSelectedTab] = useState(0);

  const handleGenerate = () => {
    generateMutation.mutate(releaseId);
  };

  const isGenerating = generateMutation.isPending;
  const hasError = error || generateMutation.isError;
  const errorMessage = error?.message || generateMutation.error?.message || 'An error occurred';

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SparklesIcon className="h-5 w-5 text-indigo-500" />
          <h2 className="text-lg font-semibold text-gray-900">AI Release Summary</h2>
        </div>
        <button
          onClick={handleGenerate}
          disabled={isGenerating || isLoading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isGenerating ? (
            <>
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
              Generating...
            </>
          ) : summary ? (
            <>
              <ArrowPathIcon className="h-4 w-4" />
              Regenerate
            </>
          ) : (
            <>
              <SparklesIcon className="h-4 w-4" />
              Generate AI Summary
            </>
          )}
        </button>
      </div>

      <div className="p-6">
        {hasError && (
          <div className="mb-4 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <ArrowPathIcon className="h-6 w-6 text-gray-400 animate-spin" />
            <span className="ml-2 text-sm text-gray-500">Loading summary...</span>
          </div>
        ) : !summary ? (
          <div className="text-center py-12">
            <SparklesIcon className="mx-auto h-10 w-10 text-gray-300" />
            <p className="mt-2 text-sm text-gray-500">
              No AI summary has been generated yet. Click "Generate AI Summary" to create one.
            </p>
          </div>
        ) : (
          <>
            <p className="text-xs text-gray-400 mb-4">
              Generated {new Date(summary.generatedAt).toLocaleString()}
            </p>
            <TabGroup selectedIndex={selectedTab} onChange={setSelectedTab}>
              <TabList className="flex space-x-1 rounded-lg bg-gray-100 p-1">
                <Tab className={({ selected }) =>
                  `w-full rounded-md py-2 text-sm font-medium leading-5 transition-colors ${
                    selected
                      ? 'bg-white text-indigo-700 shadow'
                      : 'text-gray-600 hover:bg-white/60 hover:text-gray-800'
                  }`
                }>
                  Internal Changes ({summary.internalChanges.length})
                </Tab>
                <Tab className={({ selected }) =>
                  `w-full rounded-md py-2 text-sm font-medium leading-5 transition-colors ${
                    selected
                      ? 'bg-white text-indigo-700 shadow'
                      : 'text-gray-600 hover:bg-white/60 hover:text-gray-800'
                  }`
                }>
                  Product Changes ({summary.productChanges.reduce((acc, s) => acc + s.changes.length, 0)})
                </Tab>
                <Tab className={({ selected }) =>
                  `w-full rounded-md py-2 text-sm font-medium leading-5 transition-colors ${
                    selected
                      ? 'bg-white text-indigo-700 shadow'
                      : 'text-gray-600 hover:bg-white/60 hover:text-gray-800'
                  }`
                }>
                  Customer-Facing ({summary.externalChanges.length})
                </Tab>
              </TabList>
              <TabPanels className="mt-4">
                <TabPanel>
                  {summary.internalChanges.length === 0 ? (
                    <EmptyTabContent message="No internal changes identified." />
                  ) : (
                    <>
                      {summary.summaries?.internal ? (
                        <CategorySummaryBox detail={summary.summaries.internal} items={summary.internalChanges} copyGetText={() => formatCategoryText('Internal Changes', summary.summaries?.internal, summary.internalChanges)} />
                      ) : (
                        <div className="mb-4"><CopyButton getText={() => formatCategoryText('Internal Changes', null, summary.internalChanges)} /></div>
                      )}
                      <div className="space-y-2">
                        {summary.internalChanges.map((item, idx) => (
                          <ChangeItemCard key={item.workItemId ?? idx} item={item} />
                        ))}
                      </div>
                    </>
                  )}
                </TabPanel>
                <TabPanel>
                  {summary.productChanges.length === 0 ? (
                    <EmptyTabContent message="No product-specific changes identified." />
                  ) : (
                    <>
                      {summary.summaries?.product ? (
                        <CategorySummaryBox detail={summary.summaries.product} items={summary.productChanges.flatMap(s => s.changes)} copyGetText={() => formatProductText(summary.summaries?.product, summary.productChanges)} />
                      ) : (
                        <div className="mb-4"><CopyButton getText={() => formatProductText(null, summary.productChanges)} /></div>
                      )}
                      <div className="space-y-6">
                        {summary.productChanges.map((section) => (
                          <ProductSection key={section.productName} section={section} />
                        ))}
                      </div>
                    </>
                  )}
                </TabPanel>
                <TabPanel>
                  {summary.externalChanges.length === 0 ? (
                    <EmptyTabContent message="No customer-facing changes identified." />
                  ) : (
                    <>
                      {summary.summaries?.customerFacing ? (
                        <CategorySummaryBox detail={summary.summaries.customerFacing} items={summary.externalChanges} copyGetText={() => formatCategoryText('Customer-Facing Changes', summary.summaries?.customerFacing, summary.externalChanges)} />
                      ) : (
                        <div className="mb-4"><CopyButton getText={() => formatCategoryText('Customer-Facing Changes', null, summary.externalChanges)} /></div>
                      )}
                      <div className="space-y-2">
                        {summary.externalChanges.map((item, idx) => (
                          <ChangeItemCard key={item.workItemId ?? idx} item={item} />
                        ))}
                      </div>
                    </>
                  )}
                </TabPanel>
              </TabPanels>
            </TabGroup>
          </>
        )}
      </div>
    </div>
  );
}
